import styled from '@emotion/styled';
import React, { useRef } from 'react';
import RenderLoop, { RenderItem } from '@utils/render';
import { mq } from '@styles/helpers';
import { Devices } from '@styles/tokens';
import { SwipeOutTranstion } from './SwipeOutTransition';
import { useListener } from 'react-bus';
import { MatchActionType } from '@models/MatchActionType';

const mockEntries = Array(15).fill('');

export interface IVector2 {
  x: number;
  y: number;
}

interface IDragInfo {
  startPos: IVector2;
  dragOffset: IVector2;
  isDragging: boolean;
  dragDirection: string;
}

interface IEaseInfo {
  offset: IVector2;
  rotation: number;
}

interface IPredefinedPos {
  offset: IVector2;
  rotation: number;
}

export interface IProps {
  index: number;
  children: any;
  onPick?: (direction: string, button?: boolean) => void;
}

export const SwipeDeck = (props: IProps) => {
  const outerContainer = useRef<HTMLDivElement>();
  const itemContainer = useRef<HTMLDivElement>();

  const dragTreshold: number = 100;

  const dragInfo = useRef<IDragInfo>({
    startPos: { x: 0, y: 0 },
    dragOffset: { x: 0, y: 0 },
    isDragging: false,
    dragDirection: '',
  });

  const renderItem = useRef<RenderItem>(undefined);

  const easeInfo = useRef<IEaseInfo>({
    offset: { x: 0, y: 0 },
    rotation: 0,
  });

  const stackSize: number = 2;
  const offsets: IPredefinedPos[] = [];

  while (offsets.length < stackSize) {
    offsets.push({ offset: { x: 0, y: 0 }, rotation: 0 });
  }

  React.useEffect(() => {
    updateGfx();

    if (renderItem.current) {
      renderItem.current.remove();
      renderItem.current = undefined;
    }
    renderItem.current = RenderLoop.Instance.add(render);

    return () => {
      if (renderItem.current) {
        renderItem.current.remove();
        renderItem.current = undefined;
      }
    };
  }, [props.index]);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const stackIndex: number = props.index;
    let r = itemContainer.current.children[stackIndex].getBoundingClientRect();
    if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
      onStart(e.clientX, e.clientY);
    }
  };

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const stackIndex: number = props.index;
    let r = itemContainer.current.children[stackIndex].getBoundingClientRect();
    if (
      e.touches[0].clientX >= r.left &&
      e.touches[0].clientX <= r.right &&
      e.touches[0].clientY >= r.top &&
      e.touches[0].clientY <= r.bottom
    ) {
      onStart(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  const onStart = (x, y) => {
    //console.log('onMouseDown();');

    const newStartPos: IVector2 = { x: x, y: y };

    //console.log('newStartPos : ' + JSON.stringify(newStartPos));

    dragInfo.current.dragOffset.x = 0;
    dragInfo.current.dragOffset.y = 0;

    dragInfo.current.startPos.x = newStartPos.x;
    dragInfo.current.startPos.y = newStartPos.y;

    dragInfo.current.isDragging = true;
  };

  const render = (deltaTime: number): void => {
    const newPos: IVector2 = { x: 0, y: 0 };
    let newRotation: number = 0;

    if (dragInfo.current.dragDirection === 'left' || dragInfo.current.dragDirection === 'right') {
      newPos.x = dragInfo.current.dragOffset.x;
      newPos.y = dragInfo.current.dragOffset.y / 2;
      newRotation = dragInfo.current.dragOffset.x / 20;
    } else if (dragInfo.current.dragDirection === 'up') {
      newPos.x = dragInfo.current.dragOffset.x / 2;
      newPos.y = dragInfo.current.dragOffset.y;
      newRotation = 0;
    } else {
      // do nothing
    }

    easeInfo.current.offset.x += (newPos.x - easeInfo.current.offset.x) * 0.2;
    easeInfo.current.offset.y += (newPos.y - easeInfo.current.offset.y) * 0.2;
    easeInfo.current.rotation += (newRotation - easeInfo.current.rotation) * 0.2;

    updateGfx();
  };

  const onEnd = () => {
    if (!dragInfo.current.isDragging) return;

    dragInfo.current.isDragging = false;

    //const currentPos:IVector2 = getLocalMousePosFromScreenPos({x: e.touches[0].clientX, y: e.touches[0].clientY});
    //setNewPos(currentPos);
    const children: HTMLCollection = itemContainer.current.children;
    const currentCardItem: HTMLDivElement = children[props.index] as HTMLDivElement;

    if (getDragDirection(dragInfo.current.dragOffset, dragTreshold) !== '') {
      new SwipeOutTranstion(currentCardItem, dragInfo.current.dragOffset, easeInfo.current.rotation);
      registrate(dragInfo.current.dragDirection);
    }

    const mouseUpPos: IVector2 = getLocalMousePosFromScreenPos({ x: dragInfo.current.startPos.x, y: dragInfo.current.startPos.y });
    setNewPos(mouseUpPos, true);

    // dragInfo.current.dragOffset = {x:0, y:0}
  };

  const ButtonAction = (dir: any) => {
    const children: HTMLCollection = itemContainer.current.children;
    const currentCardItem: HTMLDivElement = children[props.index] as HTMLDivElement;
    let animate: string;

    switch (dir) {
      case MatchActionType.RESET:
        animate = 'reset';
        break;
      case MatchActionType.LATE:
        animate = 'up';
        break;
      case MatchActionType.MISSING:
        animate = 'left';
        break;
      case MatchActionType.ATTENDING:
        animate = 'right';
        break;
    }

    new SwipeOutTranstion(currentCardItem, dragInfo.current.dragOffset, easeInfo.current.rotation, animate);

    props.onPick(animate, true);
  };

  useListener('onMatchNavActionSwipe', ButtonAction);

  const registrate = (direction: string): void => {
    //console.log('registrate: ' + direction);
    props.onPick(dragInfo.current.dragDirection);
  };

  const getLocalMousePosFromScreenPos = (pos: IVector2): IVector2 => {
    if (!outerContainer.current) {
      return { x: 0, y: 0 };
    }

    //const domRect:DOMRect = outerContainer.current.getBoundingClientRect();

    return { x: pos.x, y: pos.y };
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    onMove(e.clientX, e.clientY);
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    onMove(e.touches[0].clientX, e.touches[0].clientY);
  };

  const onMove = (x, y) => {
    // console.log('onWindowMouseMove();');

    if (!dragInfo.current.isDragging) return;

    const currentPos: IVector2 = getLocalMousePosFromScreenPos({ x: x, y: y });

    setNewPos(currentPos);
  };

  const setNewPos = (mousePos: IVector2, temp?: any): void => {
    const offset: IVector2 = { x: mousePos.x - dragInfo.current.startPos.x, y: dragInfo.current.startPos.y - mousePos.y };

    dragInfo.current.dragOffset.x = offset.x;
    dragInfo.current.dragOffset.y = offset.y;
    dragInfo.current.dragDirection = getDragDirection(offset, 1);

    if (temp === true) {
      dragInfo.current.isDragging = false;
    }

    // updateGfx();
  };

  const updateGfx = (): void => {
    if (!itemContainer.current) return;

    const children: HTMLCollection = itemContainer.current.children;
    const l: number = children.length;

    let i: number = 0;

    let direction = getDragDirection(easeInfo.current.offset, 1);
    //console.log("directionGFX : " + direction)

    //console.log('props.index : ' + props.index);

    let halfScreenWidth = outerContainer.current.offsetWidth / 2;
    let halfScreenHeight = outerContainer.current.offsetHeight / 2;

    //console.log("halfScreenWidth : " + halfScreenWidth)
    //console.log("halfScreenHeight : " + halfScreenHeight)
    //console.log("easeInfo.current.offset.x : " + easeInfo.current.offset.x)

    let dragRatioX = Math.abs((1 / halfScreenWidth) * dragInfo.current.dragOffset.x);
    let dragRatioY = Math.abs((1 / halfScreenHeight) * dragInfo.current.dragOffset.y);
    //console.log("dragRatioX : " + dragRatioX)
    //console.log("dragRatioY : " + dragRatioX)

    if (dragRatioX > 0.7) dragRatioX = 0.7;

    if (dragRatioY > 0.7) dragRatioY = 0.7;

    for (const child of children) {
      const currentItemTyped: HTMLDivElement = child as HTMLDivElement;

      const currentItemTypedChild: HTMLDivElement = child.children[0] as HTMLDivElement;

      const currentItemSVGAttending: HTMLElement = currentItemTypedChild.children[0] as HTMLElement;
      const currentItemSVGLate: HTMLElement = currentItemTypedChild.children[1] as HTMLElement;
      const currentItemSVGMissing: HTMLElement = currentItemTypedChild.children[2] as HTMLElement;

      currentItemTyped.style.zIndex = l - i + '';

      if (i < props.index) {
        currentItemTyped.style.display = 'none';
      } else {
        const stackIndex: number = i - props.index;

        //console.log('stackIndex : ' + stackIndex);

        if (stackIndex < stackSize) {
          // in stack
          currentItemTyped.style.display = null;

          // first card in stack
          if (stackIndex === 0) {
            if (!dragInfo.current.isDragging) {
              currentItemTyped.style.left = '0px';
              currentItemTyped.style.top = '0px';
              currentItemTyped.style.transform = `translate(-50%, -50%)`;
            } else {
              currentItemTyped.style.left = easeInfo.current.offset.x + 'px';
              currentItemTyped.style.top = -easeInfo.current.offset.y + 'px';
              currentItemTyped.style.transform = `translate(-50%, -50%) rotate(${easeInfo.current.rotation + 'deg'})`;
            }

            currentItemSVGAttending.style.opacity = `0`;
            currentItemSVGLate.style.opacity = `0`;
            currentItemSVGMissing.style.opacity = `0`;

            if (direction === 'left') {
              currentItemTypedChild.style.background = 'rgba(250, 80, 94, ' + dragRatioX / 2 + ')';
              currentItemSVGAttending.style.opacity = `${dragRatioX}`;
            }

            if (direction === 'right') {
              currentItemTypedChild.style.background = 'rgba(42, 132, 142, ' + dragRatioX / 2 + ')';
              currentItemSVGMissing.style.opacity = `${dragRatioX}`;
            }

            if (direction === 'up') {
              currentItemTypedChild.style.background = 'rgba(254, 212, 37, ' + dragRatioY / 2 + ')';
              currentItemSVGLate.style.opacity = `${dragRatioY * 2}`;
            }

            // cards visable below first card
          } else {
            const predefinedOffset: IPredefinedPos = offsets[stackIndex];

            currentItemTyped.style.left = predefinedOffset.offset.x + 'px';
            currentItemTyped.style.top = -predefinedOffset.offset.y + 'px';

            currentItemTyped.style.transform = `translate(-50%, -50%) rotate(${predefinedOffset.rotation + 'deg'})`;
          }
        } else {
          currentItemTyped.style.display = 'none';
          currentItemTypedChild.style.background = 'rgba(177, 24, 32, 0)';
        }
      }

      i += 1;
    }
  };

  const getDragDirection = (offset: IVector2, treshold: number): string => {
    const absX: number = Math.abs(offset.x);
    const absY: number = Math.abs(offset.y);

    if (absX > absY) {
      if (absX < treshold) {
        console.log('tres');
        return '';
      }

      // dragging horizontally
      if (offset.x < 0) {
        // dragging left
        return 'left';
      } else {
        // dragging right
        return 'right';
      }
    } else if (offset.y > 0) {
      // dragging up
      if (absY < treshold) {
        return '';
      }

      return 'up';
    } else {
      // dragging down - not supported
    }

    return '';
  };

  return (
    <ViewContainer
      ref={outerContainer}
      onTouchStart={onTouchStart}
      onMouseDown={onMouseDown}
      onTouchMove={onTouchMove}
      onMouseMove={onMouseMove}
      onTouchEnd={onEnd}
      onMouseUp={onEnd}
    >
      <CenterContainer ref={itemContainer}>{props.children}</CenterContainer>
      <CenterContainer id={'itemContainer'}></CenterContainer>
    </ViewContainer>
  );
};

const ViewContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: calc(var(--site-height) - var(--header-height) - var(--regi-nav-height) + 20px);
  margin: 0 auto;
  display: flex;
  align-items: center;

  ${mq(Devices.mobile)} {
    min-height: calc(var(--site-height) - var(--header-height) - var(--regi-nav-height) + 36px);
  }
`;

const CenterContainer = styled.div`
  width: 100%;
  height: 90%;
  position: absolute;
  left: 50%;
  top: 50%;
  pointer-events: none;

  ${mq(Devices.mobile)} {
    height: 98%;
  }
`;

export default SwipeDeck;
